import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../../services/api";
import "./event.css";

export default function EventList() {
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));

  const [events,        setEvents]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [participants,  setParticipants]  = useState({});
  const [loadingPart,   setLoadingPart]   = useState(null);

  /* ── load events ── */
  const loadEvents = async () => {
    try {
      setLoading(true);
      const res = await API.get("/school-admin/events/", {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setEvents(res.data.events || []);
    } catch (err) { console.error(err); setEvents([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadEvents(); }, []);

  /* ── delete ── */
  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await API.delete(`/school-admin/events/delete/${id}/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setEvents(ev => ev.filter(e => e.id !== id));
      if (expandedEvent === id) setExpandedEvent(null);
    } catch (err) { console.error(err); alert("Failed to delete event"); }
  };

  /* ── participants toggle ── */
  const toggleParticipants = async (eventId) => {
    if (expandedEvent === eventId) { setExpandedEvent(null); return; }
    if (participants[eventId]) { setExpandedEvent(eventId); return; }
    try {
      setLoadingPart(eventId);
      const res = await API.get(`/school-admin/events/${eventId}/participants/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setParticipants(p => ({ ...p, [eventId]: res.data.students || [] }));
      setExpandedEvent(eventId);
    } catch (err) { console.error(err); alert("Failed to load participants"); }
    finally { setLoadingPart(null); }
  };

  /* ── download CSV ── */
  const downloadCSV = (eventId) => {
    const students = participants[eventId] || [];
    if (!students.length) { alert("No participants to download"); return; }
    const rows    = [["Name","Class","Section","Roll"], ...students.map(s => [s.name, s.class, s.section, s.roll])];
    const csv     = rows.map(r => r.join(",")).join("\n");
    const blob    = new Blob([csv], { type: "text/csv" });
    const link    = document.createElement("a");
    link.href     = URL.createObjectURL(blob);
    link.download = `event_${eventId}_participants.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="ev-page">

      {/* ══ HERO ══ */}
      <div className="ev-hero">
        <div className="ev-hero-left">
          <div className="ev-hero-icon">🎉</div>
          <div>
            <h2 className="ev-hero-title">Events</h2>
            <p className="ev-hero-sub">School events with student participation tracking</p>
          </div>
        </div>
        <div className="ev-hero-right">
          <Link to="/school-admin/events/add" className="ev-btn-add">+ Add Event</Link>
        </div>
      </div>

      {/* ══ TABLE ══ */}
      <div className="ev-card">
        {loading ? (
          <div className="ev-empty">
            <div className="ev-empty-icon">⏳</div>
            <strong>Loading events…</strong>
          </div>
        ) : events.length === 0 ? (
          <div className="ev-empty">
            <div className="ev-empty-icon">🎪</div>
            <strong>No events yet</strong>
            <p>Create your first school event</p>
          </div>
        ) : (
          <table className="ev-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Date</th>
                <th>Description</th>
                <th>Attachment</th>
                <th>Participants</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {events.map(e => (
                <>
                  <tr key={e.id}>
                    <td><span className="ev-title-cell">{e.title}</span></td>
                    <td><span className="ev-date-badge">📅 {e.date}</span></td>
                    <td>
                      <span style={{ maxWidth:"220px", display:"inline-block", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {e.description || "—"}
                      </span>
                    </td>
                    <td>
                      {e.file
                        ? <a href={e.file} target="_blank" rel="noreferrer" className="ev-link">📎 View</a>
                        : <span className="ev-no-file">No file</span>
                      }
                    </td>
                    <td>
                      <button
                        className="ev-btn-participants"
                        onClick={() => toggleParticipants(e.id)}
                        disabled={loadingPart === e.id}
                      >
                        {loadingPart === e.id
                          ? "Loading…"
                          : expandedEvent === e.id
                            ? "▲ Hide"
                            : "👥 Students"
                        }
                      </button>
                    </td>
                    <td>
                      <button className="ev-btn-del" onClick={() => deleteEvent(e.id)}>🗑 Delete</button>
                    </td>
                  </tr>

                  {/* PARTICIPANTS PANEL */}
                  {expandedEvent === e.id && (
                    <tr key={`${e.id}-participants`}>
                      <td colSpan="6" style={{ padding: 0 }}>
                        <div className="ev-participants-panel">
                          <div className="ev-participants-head">
                            <span className="ev-participants-title">
                              🎓 Registered Students — {participants[e.id]?.length || 0}
                            </span>
                            <button className="ev-btn-csv" onClick={() => downloadCSV(e.id)}>
                              ⬇ Download CSV
                            </button>
                          </div>
                          {!participants[e.id]?.length ? (
                            <p className="ev-no-participants">No students registered yet.</p>
                          ) : (
                            <table className="ev-inner-table">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Class</th>
                                  <th>Section</th>
                                  <th>Roll</th>
                                </tr>
                              </thead>
                              <tbody>
                                {participants[e.id].map((s, i) => (
                                  <tr key={i}>
                                    <td>{s.name}</td>
                                    <td>{s.class}</td>
                                    <td>{s.section}</td>
                                    <td>{s.roll}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}
